import { RewardWalletPriority, IUserWalletRewardItem } from '@share/common-types';

const AVAILABLE_STATUS = 'Available';

function isAvailableReward(reward: IUserWalletRewardItem | undefined): reward is IUserWalletRewardItem {
  return (
    !!reward &&
    reward.status === AVAILABLE_STATUS &&
    reward.rewardValue != null &&
    reward.rewardValue > 0
  );
}

function getAvailableRewards(allRewards: IUserWalletRewardItem[] | undefined): IUserWalletRewardItem[] {
  return (allRewards ?? []).filter(isAvailableReward);
}

function getExpirationTimestamp(reward: IUserWalletRewardItem): number {
  if (!reward.expirationDate) {
    return Number.POSITIVE_INFINITY;
  }

  const timestamp = new Date(reward.expirationDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
}

function pickSoonestExpiring(
  current: IUserWalletRewardItem,
  candidate: IUserWalletRewardItem,
): IUserWalletRewardItem {
  const currentExpiry = getExpirationTimestamp(current);
  const candidateExpiry = getExpirationTimestamp(candidate);

  if (candidateExpiry < currentExpiry) {
    return candidate;
  }

  return current;
}

function pickByValue(
  current: IUserWalletRewardItem,
  candidate: IUserWalletRewardItem,
  preferHigher: boolean,
): IUserWalletRewardItem {
  const currentValue = current.rewardValue ?? 0;
  const candidateValue = candidate.rewardValue ?? 0;

  if (preferHigher) {
    if (candidateValue > currentValue) return candidate;
    if (candidateValue < currentValue) return current;
  } else {
    if (candidateValue < currentValue) return candidate;
    if (candidateValue > currentValue) return current;
  }

  return pickSoonestExpiring(current, candidate);
}

export function getNextRewardByPriority(
  allRewards: IUserWalletRewardItem[] | undefined,
  priority: RewardWalletPriority | undefined,
  nextAvailableReward?: IUserWalletRewardItem,
): IUserWalletRewardItem | undefined {
  if (
    (priority === RewardWalletPriority.Highest || priority === RewardWalletPriority.Cheapest) &&
    isAvailableReward(nextAvailableReward)
  ) {
    return nextAvailableReward;
  }

  const withValue = getAvailableRewards(allRewards);
  if (withValue.length === 0) return undefined;

  if (priority === RewardWalletPriority.Highest) {
    return withValue.reduce((best, reward) => pickByValue(best, reward, true));
  }

  if (priority === RewardWalletPriority.Cheapest) {
    return withValue.reduce((best, reward) => pickByValue(best, reward, false));
  }

  return withValue[0];
}

/**
 * Select reward by priority: Highest, Cheapest, or Best.
 * For Cheapest/Highest, prefers `nextAvailableReward` from the wallet API when present.
 * For Best, uses maxRewardCash to pick the reward that best fits (exact match, else closest from above, else largest below).
 */
export function getRewardByPriority(
  allRewards: IUserWalletRewardItem[] | undefined,
  priority: RewardWalletPriority | undefined,
  maxRewardCash?: number,
  nextAvailableReward?: IUserWalletRewardItem,
): IUserWalletRewardItem | undefined {
  if (priority === RewardWalletPriority.Best && maxRewardCash != null) {
    return getBestRewardByMaxRewardCash(allRewards, maxRewardCash);
  }

  return getNextRewardByPriority(allRewards, priority, nextAvailableReward);
}

/**
 * Select the "best" reward: the one whose rewardValue is closest to maxRewardCash.
 * - If any reward has rewardValue === maxRewardCash, select the one expiring soonest among ties.
 * - If there are values > maxRewardCash, select the smallest among those (closest from above).
 * - If all values < maxRewardCash, select the one with the biggest rewardValue.
 * When multiple rewards share the same amount, the one expiring soonest is preferred.
 */
export function getBestRewardByMaxRewardCash(
  allRewards: IUserWalletRewardItem[] | undefined,
  maxRewardCash: number,
): IUserWalletRewardItem | undefined {
  const withValue = getAvailableRewards(allRewards);
  if (withValue.length === 0) return undefined;

  const exactMatches = withValue.filter((reward) => (reward.rewardValue ?? 0) === maxRewardCash);
  if (exactMatches.length > 0) {
    return exactMatches.reduce(pickSoonestExpiring);
  }

  const greaterOrEqual = withValue.filter((reward) => (reward.rewardValue ?? 0) >= maxRewardCash);
  if (greaterOrEqual.length > 0) {
    return greaterOrEqual.reduce((best, reward) => pickByValue(best, reward, false));
  }

  return withValue.reduce((best, reward) => pickByValue(best, reward, true));
}
